import type { StationDetailsProps } from '@metro/station-details'
import type { SchemaData, SchemaStationTimetable } from '../schema/types'
import { detailsTimetable } from './detailsTimetable'

interface StationLike {
  id: string
  localName: string
  line: { localName?: string | null; hexColor: string }
  zone?: string | null
  facilities?: ReadonlyArray<string>
  stationTimetable?: SchemaStationTimetable
}

type Transfers = StationDetailsProps['transfers']

export interface StationDetailsHandlers {
  onTransferTap: StationDetailsProps['onTransferTap']
  onFavoriteTap: StationDetailsProps['onFavoriteTap']
  onPaywallTap: StationDetailsProps['onPaywallTap']
  onReportIssueTap: StationDetailsProps['onReportIssueTap']
  /** Re-dispatch the station load (S4 Retry). Defaults to a no-op. */
  onRetry?: StationDetailsProps['onRetry']
  /**
   * Current favourite state of the station, read from the shared
   * `FavoritesService` (TASK-318 §16). Defaults to `false` so existing
   * call sites that don't track favourites keep their behaviour.
   */
  isFavorite?: boolean
  /**
   * Premium entitlement. Web has no premium tier wired yet, so this
   * defaults to `false` (shows the upsell banner — the previous
   * hidden-gate behaviour). Flip when the web entitlement lands.
   */
  isPremium?: boolean
  /** Reachability — drives the S5 offline banner. Defaults to online. */
  isOffline?: boolean
}

/**
 * Build the full prop bag for `<StationDetails>` from a schema station
 * record + ambient handlers/transfers. Used by both the inline tablet+
 * mount in StationCardOrDetailsRouter and the mobile modal mount in
 * AppShell — keeps the two surfaces in lockstep without duplicating the
 * defensive-cast logic for `coordinate` (the field exists on the
 * runtime schema.json but stays optional in `SchemaData` for stripped
 * fixtures) and the timetable adapter ({@link detailsTimetable}).
 */
export function buildStationDetailsProps(
  station: StationLike,
  _schema: SchemaData,
  transfers: Transfers,
  handlers: StationDetailsHandlers,
): StationDetailsProps {
  const runtime = station as unknown as {
    coordinate?: { latitude: number; longitude: number }
  }
  return {
    stationId: station.id,
    stationName: station.localName,
    lineLocalName: station.line.localName ?? '',
    lineHexColor: station.line.hexColor,
    zone: station.zone ?? null,
    lat: runtime.coordinate?.latitude ?? 0,
    lng: runtime.coordinate?.longitude ?? 0,
    facilities: station.facilities ?? [],
    timetable: detailsTimetable(station.stationTimetable),
    transfers,
    isFavorite: handlers.isFavorite ?? false,
    isPremium: handlers.isPremium ?? false,
    isOffline: handlers.isOffline ?? false,
    onTransferTap: handlers.onTransferTap,
    onFavoriteTap: handlers.onFavoriteTap,
    onPaywallTap: handlers.onPaywallTap,
    onReportIssueTap: handlers.onReportIssueTap,
    onRetry: handlers.onRetry ?? ((): void => {}),
  }
}
